// BookingSteps — inner steps of the home booking widget.
//
// • ExtrasStep: mounts the shared <ExtrasGrid> so the catalogue, tiles
//   and selection state are byte-identical to the /booking page.
// • DetailsStep: slimmed-down contact form (4 fields + message) with
//   validation errors from the shared BookingContext.
//
// Both surfaces read/write directly from BookingContext — no local
// state — so the widget stays in perfect sync with the standalone
// /booking flow and the floating summary.

import { useBooking } from "@/contexts/BookingContext";
import ExtrasGrid from "@/components/booking/ExtrasGrid";

const pick = (obj, lang) => (obj?.[lang] ?? obj?.en ?? "");

const COPY = {
  fld_full_name: { en: "Full name", fr: "Nom complet", es: "Nombre completo" },
  fld_email: { en: "Email", fr: "E-mail", es: "Correo electrónico" },
  fld_phone: { en: "Phone", fr: "Téléphone", es: "Teléfono" },
  fld_whatsapp: { en: "WhatsApp (optional)", fr: "WhatsApp (optionnel)", es: "WhatsApp (opcional)" },
  fld_message: { en: "Notes & dreams", fr: "Notes & rêves", es: "Notas y sueños" },
  fld_message_ph: {
    en: "Tell us about the trip you're imagining — slow days, dune evenings, the textures you'd like.",
    fr: "Parlez-nous du voyage que vous imaginez — journées lentes, soirées dans les dunes, les textures que vous aimeriez.",
    es: "Cuéntanos el viaje que imaginas — días lentos, atardeceres en las dunas, las texturas que deseas.",
  },
};

export const ExtrasStep = ({ lang }) => {
  const { selectedServices, toggleService, updateServiceDetail } = useBooking();
  return (
    <div data-testid="widget-step-extras">
      <ExtrasGrid
        lang={lang}
        selectedServices={selectedServices}
        toggleService={toggleService}
        updateServiceDetail={updateServiceDetail}
        testIdPrefix="widget"
        columns="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
      />
    </div>
  );
};

export const DetailsStep = ({ lang, nameRef, emailRef, phoneRef }) => {
  const { form, setFormField, fieldErrors } = useBooking();
  const inputBase =
    "w-full bg-[#FDFBF7] border px-4 py-3 text-[14px] text-[#2C2621] placeholder:text-[#5C5248]/45 focus:outline-none transition-colors";
  const cls = (key) =>
    fieldErrors?.[key]
      ? `${inputBase} border-[#B7301B] bg-[#FBEFEA] focus:border-[#B7301B]`
      : `${inputBase} border-[#2C2621]/15 hover:border-[#D4A373] focus:border-[#C16542]`;
  const labelCls = "text-[10px] tracking-[0.3em] uppercase text-[#5C5248] mb-2";

  return (
    <div className="space-y-4" data-testid="widget-step-details">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
        <label className="flex flex-col">
          <span className={labelCls}>{pick(COPY.fld_full_name, lang)}</span>
          <input
            ref={nameRef}
            id="widget-input-name"
            data-testid="widget-input-name"
            type="text"
            required
            aria-required="true"
            aria-invalid={fieldErrors?.full_name ? "true" : "false"}
            value={form.full_name}
            onChange={(e) => setFormField("full_name", e.target.value)}
            className={cls("full_name")}
          />
          {fieldErrors?.full_name ? (
            <span
              role="alert"
              className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-[#B7301B] leading-snug"
              data-testid="widget-input-name-error"
            >
              <span aria-hidden="true" className="inline-block w-1 h-1 rounded-full bg-[#B7301B]" />
              {fieldErrors.full_name}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col">
          <span className={labelCls}>{pick(COPY.fld_email, lang)}</span>
          <input
            ref={emailRef}
            id="widget-input-email"
            data-testid="widget-input-email"
            type="email"
            required
            aria-required="true"
            aria-invalid={fieldErrors?.email ? "true" : "false"}
            value={form.email}
            onChange={(e) => setFormField("email", e.target.value)}
            className={cls("email")}
          />
          {fieldErrors?.email ? (
            <span
              role="alert"
              className="mt-1.5 inline-flex items-center gap-1.5 text-[12px] text-[#B7301B] leading-snug"
              data-testid="widget-input-email-error"
            >
              <span aria-hidden="true" className="inline-block w-1 h-1 rounded-full bg-[#B7301B]" />
              {fieldErrors.email}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col">
          <span className={labelCls}>{pick(COPY.fld_phone, lang)}</span>
          <input
            ref={phoneRef}
            data-testid="widget-input-phone"
            type="tel"
            value={form.phone}
            onChange={(e) => setFormField("phone", e.target.value)}
            className={cls("phone")}
          />
        </label>

        <label className="flex flex-col">
          <span className={labelCls}>{pick(COPY.fld_whatsapp, lang)}</span>
          <input
            data-testid="widget-input-whatsapp"
            type="text"
            value={form.whatsapp}
            onChange={(e) => setFormField("whatsapp", e.target.value)}
            className={cls("whatsapp")}
          />
        </label>
      </div>

      <label className="flex flex-col">
        <span className={labelCls}>{pick(COPY.fld_message, lang)}</span>
        <textarea
          data-testid="widget-input-message"
          rows={4}
          placeholder={pick(COPY.fld_message_ph, lang)}
          value={form.message}
          onChange={(e) => setFormField("message", e.target.value)}
          className={cls("message") + " resize-none"}
        />
      </label>
    </div>
  );
};
